import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Modal, Select, Input, InputNumber, Upload, Alert, Spin, Button } from "antd";
import { UploadOutlined, PaperClipOutlined } from "@ant-design/icons";
import { usePaymentMethods, useRecordOfflinePayment } from "../hooks/useProposalSends";
import { fetchBanks } from "../services/orderService";

/**
 * Money that came in outside the payment link.
 *
 * A quotation can be paid two ways. Online, the client uses the link in their
 * own document and the gateway's webhook activates the subscription — nobody
 * fills anything in, and this form is not offered. Offline, someone transfers to
 * the bank or hands over a cheque, and only the rep who took it knows. This is
 * where they say so.
 *
 * What it records is a claim, not a fact: it goes in as pending and accounts
 * verify it before anything activates. That is why the form asks for a
 * reference and a receipt — those are what makes verification possible.
 */

const label = {
  fontSize: 12, fontWeight: 700, color: "#374151",
  display: "block", marginBottom: 6, marginTop: 14,
};
const hint = { fontSize: 11.5, color: "#9ca3af", fontWeight: 400 };

/* What a bank reference actually looks like — the same bounds the order screens
   enforce, so one payment cannot be recorded two ways. */
const UTR_MIN = 12;
const UTR_MAX = 22;

const inr = (n) => {
  const value = Number(n);
  if (!Number.isFinite(value)) return "—";
  const paise = Math.abs(value % 1) > 0;
  return `₹${value.toLocaleString("en-IN", {
    minimumFractionDigits: paise ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
};

const OfflinePaymentModal = ({ row, open, onAccepted, onClose }) => {
  const { data: methods = [], isLoading: methodsLoading, error: methodsError } =
    usePaymentMethods();
  const record = useRecordOfflinePayment();

  /* The same bank master the order screens use, so a name recorded here matches
     one recorded there and accounts are reconciling against one list. Keyed on
     the bank's name because that is the field the API wants; the master is the
     one place it is spelled. */
  const { data: banks = [], isLoading: banksLoading } = useQuery({
    queryKey: ["bank-master"],
    queryFn: fetchBanks,
    staleTime: 1000 * 60 * 60,
  });
  const bankOptions = useMemo(
    () => banks.map((b) => ({ value: b.label, label: b.label })),
    [banks],
  );

  const [mode, setMode] = useState(null);
  const [amount, setAmount] = useState(null);
  const [reference, setReference] = useState("");
  const [bank, setBank] = useState("");
  const [proof, setProof] = useState(null);
  const [reminderDays, setReminderDays] = useState(7);

  /* This form is about what is still owed, not what was originally quoted.
     A client paying the second instalment of three has a balance the API has
     already worked out — measuring against the full total instead would tell the
     rep the whole amount is outstanding and mark a settling payment as partial.
     `amount` is only the fallback for a quotation nothing has been paid on.

     Onboarding is not asked for here: it was priced on the proposal and is
     already inside this quotation's total, so a second box for it would only
     invite a figure that disagrees with the document the client was sent. */
  const total = Number(row?.amount) || 0;
  const collected = Number(row?.amount_received) || 0;
  const outstanding = row?.amount_due == null ? total : Number(row.amount_due) || 0;

  const paid = Number(amount) || 0;
  const remaining = useMemo(
    () => Math.max(0, outstanding - paid),
    [outstanding, paid],
  );
  const isPartial = paid > 0 && remaining > 0;

  const reset = () => {
    setMode(null); setAmount(null);
    setReference(""); setBank(""); setProof(null); setReminderDays(7);
  };

  const close = () => { reset(); onClose(); };

  /* The quotation is accepted because one of these two answers was given, not
     because the form was opened — so the status is written here, after the
     answer, and never on the way in. */
  const acceptAndClose = () => { onAccepted?.(); close(); };

  const submit = () => {
    record.mutate(
      {
        id: row.id,
        fields: {
          payment_mode: mode,
          amount_received: paid,
          /* No `onboarding_amount`. It was set on the proposal and is part of
             what this quotation asks for, so the server already has it — sending
             a second copy from here could only ever contradict the first. */
          utr: reference,
          bank_name: bank,
          /* Sent with the claim rather than uploaded separately — a receipt that
             travels in its own request is one that can go missing between the
             two. */
          ...(proof ? { payment_proof: proof } : {}),
          /* Always sent, including on a payment that settles in full: a balance
             can still appear later — a bounced cheque, a short transfer — and
             the reminder window has to already be on the record when it does. */
          remaining_reminder_days: reminderDays,
        },
      },
      { onSuccess: acceptAndClose },
    );
  };

  /* Named rather than left to a greyed-out button: a rep who cannot see why it
     is disabled will read it as broken and go looking for someone. */
  const referenceOk = reference.length >= UTR_MIN && reference.length <= UTR_MAX;
  const missing = [
    !mode && "the payment method",
    !(paid > 0) && "an amount",
    !referenceOk && `a ${UTR_MIN}–${UTR_MAX} character UTR / reference`,
    !proof && "the payment proof",
  ].filter(Boolean);
  const canSubmit = missing.length === 0 && !record.isPending;

  return (
    <Modal
      open={open}
      onCancel={close}
      title={(
        <div>
          <div style={{ fontWeight: 700, fontSize: 15 }}>Payment received (offline)?</div>
          {/* The balance leads, because that is the number this form is about.
              What has already come in is named beside it so the rep can see the
              balance is a remainder and not the whole quotation. */}
          <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 400, marginTop: 2 }}>
            {row?.client_name || "This client"} · {inr(outstanding)} to collect
            {collected > 0 && ` · ${inr(collected)} of ${inr(total)} already received`}
          </div>
        </div>
      )}
      width={520}
      /* Three separate outcomes, so they cannot share a button. Both answers
         accept the quotation; closing the dialog is not an answer and leaves the
         row exactly as it was. */
      footer={(
        <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
          <Button onClick={close} disabled={record.isPending}>Not now</Button>
          <div style={{ display: "flex", gap: 10 }}>
            <Button onClick={acceptAndClose} disabled={record.isPending}>
              They will pay online
            </Button>
            <Button
              type="primary"
              onClick={submit}
              disabled={!canSubmit}
              loading={record.isPending}
            >
              Record payment &amp; accept
            </Button>
          </div>
        </div>
      )}
    >
      {/* Said up front, because a rep who expects this to activate the
          subscription will not chase accounts for the approval. */}
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 4 }}
        message="This goes in as pending verification"
        description="Accounts approve it before the subscription activates. Nothing here charges the client."
      />

      <label style={label}>Payment method *</label>
      {methodsError ? (
        <Alert
          type="error"
          showIcon
          message="Payment methods could not be loaded"
          description={methodsError.message}
        />
      ) : (
        <Select
          value={mode}
          onChange={setMode}
          placeholder={methodsLoading ? "Loading…" : "How did the money arrive?"}
          style={{ width: "100%" }}
          options={methods}
          loading={methodsLoading}
          notFoundContent={methodsLoading ? <Spin size="small" /> : "No methods configured"}
        />
      )}

      <label style={label}>
        Amount received * <span style={hint}>· part payments are fine</span>
      </label>
      <InputNumber
        value={amount}
        onChange={setAmount}
        min={0}
        style={{ width: "100%" }}
        placeholder={String(outstanding || 0)}
        prefix="₹"
      />
      {/* Measured against the balance, so on a second instalment this says what
          is left after both payments — not after this one alone. */}
      {isPartial && (
        <div style={{ fontSize: 12, color: "#b45309", marginTop: 6, fontWeight: 600 }}>
          {inr(remaining)} still outstanding after this.
        </div>
      )}
      {paid > 0 && remaining === 0 && (
        <div style={{ fontSize: 12, color: "#16a34a", marginTop: 6, fontWeight: 600 }}>
          Settles the quotation in full.
        </div>
      )}
      {paid > outstanding && (
        <div style={{ fontSize: 12, color: "#b45309", marginTop: 6 }}>
          That is {inr(paid - outstanding)} more than is owed — worth checking
          before accounts see it.
        </div>
      )}

      {/* Required, both of them: without a reference to match and a document to
          match it against, accounts have nothing to verify and the payment sits
          pending until someone goes back to the rep for it. */}
      <label style={label}>
        UTR / reference * <span style={hint}>· what accounts will match against</span>
      </label>
      <Input
        value={reference}
        /* Same rule as the order screens: letters and digits only, 12–22 of
           them. A reference recorded here has to look like the one recorded
           there, or accounts are matching two different formats against one
           bank statement. */
        onChange={(e) => setReference(e.target.value.replace(/[^a-zA-Z0-9]/g, ""))}
        maxLength={UTR_MAX}
        placeholder={`Enter UTR number (${UTR_MIN}–${UTR_MAX} characters)`}
      />
      <div style={{
        fontSize: 11, marginTop: 4, textAlign: "right",
        color: referenceOk ? "#16a34a" : "#9ca3af",
      }}>
        {reference.length} / {UTR_MAX} characters
        {referenceOk && <span style={{ marginLeft: 6 }}>✓ Valid length</span>}
      </div>

      <label style={label}>Bank name</label>
      <Select
        value={bank || undefined}
        onChange={setBank}
        allowClear
        showSearch
        optionFilterProp="label"
        style={{ width: "100%" }}
        placeholder={banksLoading ? "Loading banks…" : "Which bank the money landed in"}
        options={bankOptions}
        loading={banksLoading}
        notFoundContent={banksLoading ? <Spin size="small" /> : "No banks configured"}
      />

      <label style={label}>
        Payment proof * <span style={hint}>· screenshot, receipt, statement</span>
      </label>
      <Upload
        maxCount={1}
        // Held back and sent with the form; nothing is uploaded on selection.
        beforeUpload={(file) => { setProof(file); return false; }}
        onRemove={() => setProof(null)}
        fileList={proof ? [{ uid: "1", name: proof.name, status: "done" }] : []}
      >
        <button
          type="button"
          style={{
            display: "inline-flex", alignItems: "center", gap: 7, height: 34,
            padding: "0 14px", borderRadius: 8, border: "1px solid #ddd6fe",
            background: "#f5f3ff", color: "#5b48e8", fontSize: 13,
            fontWeight: 600, cursor: "pointer",
          }}
        >
          {proof ? <PaperClipOutlined /> : <UploadOutlined />}
          {proof ? "Replace file" : "Attach proof"}
        </button>
      </Upload>

      {/* Shown even when the payment settles in full, because it is stored
          either way — a setting saved from a field nobody saw is the kind of
          thing that surprises someone months later. */}
      <label style={label}>
        Remind about any balance in{" "}
        <span style={hint}>
          {isPartial ? "· days from today" : "· only used if a balance appears"}
        </span>
      </label>
      <InputNumber
        value={reminderDays}
        onChange={setReminderDays}
        min={1}
        style={{ width: "100%" }}
        addonAfter="days"
      />

      {missing.length > 0 && (
        <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 16 }}>
          Still needs {missing.join(", ").replace(/, ([^,]*)$/, " and $1")}.
        </div>
      )}
    </Modal>
  );
};

export default OfflinePaymentModal;
